import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { fetchFeatureSwitches } from '@/api/client'
import { Layout } from '@/components/Layout'
import { RequireAuth } from '@/auth/RequireAuth'
import { AuthCallbackPage } from '@/features/auth/AuthCallbackPage'
import { SignInPage } from '@/features/auth/SignInPage'
import { OpportunityListPage } from '@/features/opportunities/OpportunityListPage'
import { FinanceConsolePage } from '@/features/console/FinanceConsolePage'
import { AlertsPage } from '@/features/alerts/AlertsPage'
import { AdminIndexRedirect, AdminLayout } from '@/features/admin/AdminLayout'
import { AlertsAdminPage } from '@/features/admin/AlertsAdminPage'
import { ClosedMonthsAdminPage } from '@/features/admin/ClosedMonthsAdminPage'
import { DriversAdminPage } from '@/features/admin/DriversAdminPage'
import { StagesAdminPage } from '@/features/admin/StagesAdminPage'
import { SystemAdminPage } from '@/features/admin/SystemAdminPage'
import { ViewsAdminPage } from '@/features/admin/ViewsAdminPage'
import { CustomFieldsAdminPage } from '@/features/admin/CustomFieldsAdminPage'
import { HygieneAdminPage } from '@/features/admin/HygieneAdminPage'
import { RevenueRulesAdminPage } from '@/features/admin/RevenueRulesAdminPage'
import { YearsAdminPage } from '@/features/admin/YearsAdminPage'
import { ConfigTransferAdminPage } from '@/features/admin/ConfigTransferAdminPage'
import { AccessLogAdminPage } from '@/features/admin/AccessLogAdminPage'
import { AiLogAdminPage } from '@/features/admin/AiLogAdminPage'
import { CustomersAdminPage } from '@/features/admin/CustomersAdminPage'
import { UsersAdminPage } from '@/features/admin/UsersAdminPage'
import { EmailFailuresAdminPage } from '@/features/admin/EmailFailuresAdminPage'
import { PublishedPage } from '@/features/published/PublishedPage'
import { MonthlyForecastPage } from '@/features/forecast/MonthlyForecastPage'
import { AppChromeProvider } from '@/context/AppChromeContext'
import { useAuth } from '@/auth/useAuth'
import { isFinanceUser, isViewerUser } from '@/api/types'

function RequireFinance({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  if (!isFinanceUser(user)) {
    return <Navigate to="/opportunities" replace />
  }
  return children
}

function RequireViewer({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  if (!isViewerUser(user) && !isFinanceUser(user)) {
    return <Navigate to="/opportunities" replace />
  }
  return children
}

function RequirePhase2Ui({ children }: { children: ReactNode }) {
  const switchesQuery = useQuery({
    queryKey: ['config', 'feature-switches'],
    queryFn: fetchFeatureSwitches,
  })
  if (switchesQuery.isLoading) {
    return null
  }
  if (switchesQuery.data?.feature_switches?.phase2_ui !== true) {
    return <Navigate to="/admin/stages" replace />
  }
  return children
}

const router = createBrowserRouter([
  {
    path: '/sign-in',
    element: <SignInPage />,
  },
  {
    path: '/auth/callback',
    element: <AuthCallbackPage />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppChromeProvider>
          <Layout />
        </AppChromeProvider>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/opportunities" replace /> },
      { path: 'opportunities', element: <OpportunityListPage /> },
      { path: 'forecast', element: <MonthlyForecastPage /> },
      { path: 'alerts', element: <AlertsPage /> },
      {
        path: 'console',
        element: (
          <RequireFinance>
            <FinanceConsolePage />
          </RequireFinance>
        ),
      },
      {
        path: 'admin',
        element: (
          <RequireFinance>
            <AdminLayout />
          </RequireFinance>
        ),
        children: [
          { index: true, element: <AdminIndexRedirect /> },
          { path: 'stages', element: <StagesAdminPage /> },
          { path: 'views', element: <ViewsAdminPage /> },
          { path: 'years', element: <YearsAdminPage /> },
          { path: 'drivers', element: <DriversAdminPage /> },
          { path: 'custom-fields', element: <CustomFieldsAdminPage /> },
          { path: 'revenue-rules', element: <RevenueRulesAdminPage /> },
          { path: 'hygiene', element: <HygieneAdminPage /> },
          { path: 'alerts', element: <AlertsAdminPage /> },
          { path: 'closed-months', element: <ClosedMonthsAdminPage /> },
          { path: 'config-transfer', element: <ConfigTransferAdminPage /> },
          { path: 'access-log', element: <AccessLogAdminPage /> },
          {
            path: 'ai-log',
            element: (
              <RequirePhase2Ui>
                <AiLogAdminPage />
              </RequirePhase2Ui>
            ),
          },
          { path: 'customers', element: <CustomersAdminPage /> },
          { path: 'users', element: <UsersAdminPage /> },
          { path: 'email-failures', element: <EmailFailuresAdminPage /> },
          { path: 'system', element: <SystemAdminPage /> },
        ],
      },
      {
        path: 'published',
        element: (
          <RequireViewer>
            <PublishedPage />
          </RequireViewer>
        ),
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
])

export function App() {
  return <RouterProvider router={router} />
}
